<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="categories-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('categories.title') }}</h1>
          <p class="page-subtitle">Organize your products by category</p>
        </div>
        <IntercomButton variant="primary" @click="showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('categories.addCategory') }}</span>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Categories Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="categories"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleView"
        >
          <template #cell-description="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell-product_count="{ value }">
            {{ value || 0 }}
          </template>

          <template #cell-actions="{ row }">
            <div style="display: flex; gap: var(--space-2)">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleEdit(row)">
                {{ $t('common.edit') }}
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDelete(row)">
                {{ $t('common.delete') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Modal -->
      <CategoryForm
        v-model:show="showCreateModal"
        :category="selectedCategory"
        @success="handleFormSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomPagination,
} from '@shared/components/intercom'
import CategoryForm from '../components/CategoryForm.vue'
import { billingNavSections } from '../config/navigation.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listCategories, deleteCategory, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: categories,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listCategories(params))

const showCreateModal = ref(false)
const selectedCategory = ref(null)
const searchQuery = ref('')

// Table columns
const columns = [
  { key: 'name', title: t('categories.name'), width: '250px' },
  { key: 'description', title: t('categories.description'), width: '350px' },
  { key: 'product_count', title: 'Products', width: '100px', align: 'center' },
  { key: 'actions', title: 'Actions', width: '150px', align: 'right' },
]

/**
 * Navigate to category detail view
 */
const handleView = (category) => {
  router.push(`/categories/${category.id}`)
}

/**
 * Handle edit category
 */
const handleEdit = (category) => {
  selectedCategory.value = category
  showCreateModal.value = true
}

/**
 * Handle delete category
 */
const handleDelete = async (category) => {
  const ok = await confirm(`Delete category "${category.name}"?`, { title: 'Delete Category', type: 'error' })
  if (!ok) {
    return
  }

  try {
    await deleteCategory(category.id)
    await fetch()
  } catch (error) {
    console.error('Failed to delete category:', error)
    // TODO: Show error notification
  }
}

/**
 * Handle form success (create/update)
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  selectedCategory.value = null
  await fetch()
}

const handleSearchInput = () => {
  resetAndFetch({ search: searchQuery.value })
}

const clearSearch = () => {
  searchQuery.value = ''
  resetAndFetch({ search: '' })
}

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

// usePaginatedList auto-fetches on creation (immediate: true)
onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.categories-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--color-gray-400);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-10) 0 44px;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  outline: none;
  transition: all var(--transition-fast);
}

.search-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.search-clear {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-gray-100);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-gray-500);
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-gray-200);
  color: var(--color-gray-700);
}

@media (max-width: 768px) {
  .categories-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }
}
</style>
